import { useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { ReactComponent as SearchIcon } from "../../../assets/icons/dashboard/user/searchIcon.svg";
import AddButton from "../../../components/AddButton";
import CustomizeColumns from "./components/CustomizeColumns";
import TableLayout from "../../../components/TableLayout";
import api from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext"; // ✅ Import AuthContext

export default function Perkataan() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth(); // ✅ Get user + permissions

  // 🔹 State
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // 🔹 Define all table columns
  const allColumns = [
    { key: "KategoriBudaya", label: "Kategori Budaya" },
    { key: "Perkataan", label: "Perkataan" },
    { key: "AyatAlQuran", label: "Ayat Al-Quran" },
    { key: "TerjemahanAyat", label: "Terjemahan Ayat" },
    { key: "Denotasi", label: "Denotasi" },
    { key: "Konotasi", label: "Konotasi" },
    { key: "TafsiranUlama1", label: "Tafsiran Ulama 1" },
    { key: "TafsiranUlama2", label: "Tafsiran Ulama 2" },
  ];

  // 🔹 Default visible columns
  const [visibleColumns, setVisibleColumns] = useState([
    "KategoriBudaya",
    "Perkataan",
    "TafsiranUlama1",
    "TafsiranUlama2",
  ]);

  // 🔹 Permissions (view handled at layout level)
  const canCreate = user?.permissions?.perkataan?.create;
  const canEdit = user?.permissions?.perkataan?.edit;
  const canDelete = user?.permissions?.perkataan?.delete;

  // 🔹 Navigate to Add Perkataan
  const navigateToAddPerkataan = () => {
    navigate("/add-perkataan");
  };

  useEffect(() => {
    const fetchPerkataan = async () => {
      try {
        const res = await api.get("/perkataan");
        if (res.data.success) {
          let data = res.data.data || [];

          // 🆕 Sort by PerkataanID (latest first)
          data = data.sort((a, b) => b.PerkataanID - a.PerkataanID);

          const mappedRows = data.map((item) => ({
            id: item.PerkataanID,
            KategoriBudaya: item.KategoriBudaya,
            Perkataan: item.Perkataan,
            AyatAlQuran: item.AyatAlQuran ? "Available" : "N/A",
            TerjemahanAyat: item.TerjemahanAyat || "N/A",
            Denotasi: item.Denotasi,
            Konotasi: item.Konotasi,
            TafsiranUlama1: item.TafsiranUlama1,
            TafsiranUlama2: item.TafsiranUlama2,
          }));

          setRows(mappedRows);
        }
      } catch (err) {
        console.error("Error fetching perkataan:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPerkataan();
  }, []);

  // 🔹 Delete handler
  const handleDelete = async (id) => {
    try {
      await api.delete(`/perkataan/${id}`);
      setRows((prev) => prev.filter((row) => row.id !== id));

      setSnackbar({
        open: true,
        message: "Successfully deleted perkataan!",
        severity: "success",
      });
    } catch (err) {
      console.error("Delete failed:", err.message);
      setSnackbar({
        open: true,
        message: "Failed to delete perkataan",
        severity: "error",
      });
    }
  };

  // 🔹 Search filter
  const filteredRows = rows.filter((row) => {
    const kategori = row.KategoriBudaya ? row.KategoriBudaya.toString() : "";
    const perkataan = row.Perkataan ? row.Perkataan.toString() : "";
    const term = searchTerm.trim().toLowerCase();

    return (
      kategori.toLowerCase().includes(term) ||
      perkataan.toLowerCase().includes(term)
    );
  });

  // 🔹 JSX return
  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between"
        style={{ padding: "18px" }}
      >
        <div className="font-inter text-[18px] font-semibold !pl-3">
          Perkataan
        </div>

        <div className="flex gap-5 items-center">
          {/* 🔍 Search Input */}
          <Box className="relative w-[246px]">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by Kategori Budaya or Perkataan"
              className="text-[14px] placeholder:text-[11px] border rounded-[5px] pl-10 pr-2 p-2 h-[40px] w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          {/* 🔧 Customize Columns */}
          <CustomizeColumns
            label="Customize Columns"
            allColumns={allColumns}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          />

          {/* ➕ Add button (only if allowed) */}
          {canCreate && (
            <AddButton label="Add Perkataan" onClick={navigateToAddPerkataan} />
          )}
        </div>
      </div>

      {/* 📊 Table Section */}
      <div className="px-5">
        <TableLayout
          columns={allColumns}
          visibleColumns={visibleColumns}
          rows={filteredRows}
          loading={loading}
          onEdit={canEdit ? (id) => navigate(`/edit-perkataan/${id}`) : null}
          onDelete={canDelete ? handleDelete : null}
        />
      </div>

      {/* ✅ Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
